import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminSidebar from '../../components/AdminSidebar';
import BilingualInput from '../../components/admin/BilingualInput';
import BilingualTextArea from '../../components/admin/BilingualTextArea';
import ClientsEditorFields from '../../components/admin/ClientsEditorFields';
import ClientLogoUploadGrid from '../../components/admin/ClientLogoUploadGrid';
import ServiceCardManager from '../../components/admin/ServiceCardManager';
import {
    Building2,
    Home,
    Type,
    PanelBottom,
    Mail,
    HardHat,
    DollarSign,
    Zap,
    LogOut,
    Loader2,
    CheckCircle2,
    Save,
    Layout,
    FileText,
    Image as ImageIcon,
    Mountain,
    Plane,
    Star,
    MapPin,
    RefreshCw,
    AlertCircle,
    Target,
    Truck,
    Info,
    Users,
    Wrench
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

const AdminPlaza: React.FC = () => {
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState<'content' | 'images'>('content');
    const [activeSection, setActiveSection] = useState<'banner' | 'about' | 'services' | 'clients' | 'contact'>('banner');
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState<string | null>(null);
    const [content, setContent] = useState<any>({ Plaza: {} });
    const [pendingImages, setPendingImages] = useState<{ [key: string]: { file: File, preview: string } }>({});
    const [message, setMessage] = useState<{ text: string, type: 'success' | 'error' | '' }>({ text: '', type: '' });
    const username = localStorage.getItem('adminUser') || 'Admin';

    useEffect(() => {
        const token = localStorage.getItem('adminToken');
        if (!token) {
            navigate('/admin/login');
        } else {
            fetchContent();
        }
    }, [navigate]);

    const fetchContent = async () => {
        setLoading(true);
        try {
            const response = await fetch('/api/homepage/content');
            const data = await response.json();
            if (!data.Plaza) data.Plaza = {};
            setContent(data);
        } catch (err) {
            console.error('Failed to fetch content');
        } finally {
            setLoading(false);
        }
    };

    const handleLogout = () => {
        localStorage.removeItem('adminToken');
        localStorage.removeItem('adminUser');
        navigate('/admin/login');
    };

    const updateField = (key: string, value: any) => {
        setContent((prev: any) => ({
            ...prev,
            Plaza: {
                ...prev.Plaza,
                [key]: value
            }
        }));
    };

    const saveContent = async () => {
        setSaving('Plaza');
        try {
            const plazaData = content.Plaza || {};
            
            // Define fields to save based on active section
            let fieldsToSave: { [key: string]: string } = {};
            if (activeSection === 'banner') {
                fieldsToSave = {
                    banner_title: plazaData.banner_title || '',
                    banner_subtitle: plazaData.banner_subtitle || ''
                };
            } else if (activeSection === 'about') {
                fieldsToSave = {
                    about_title: plazaData.about_title || '',
                    about_desc: plazaData.about_desc || '',
                    about_website: plazaData.about_website || '',
                    about_pt_1_title: plazaData.about_pt_1_title || '',
                    about_pt_1_desc: plazaData.about_pt_1_desc || '',
                    about_pt_2_title: plazaData.about_pt_2_title || '',
                    about_pt_2_desc: plazaData.about_pt_2_desc || '',
                    about_pt_3_title: plazaData.about_pt_3_title || '',
                    about_pt_3_desc: plazaData.about_pt_3_desc || ''
                };
            } else if (activeSection === 'services') {
                fieldsToSave = {
                    services_title: plazaData.services_title || '',
                    svc_1_title: plazaData.svc_1_title || '',
                    svc_1_desc: plazaData.svc_1_desc || '',
                    svc_2_title: plazaData.svc_2_title || '',
                    svc_2_desc: plazaData.svc_2_desc || '',
                    svc_3_title: plazaData.svc_3_title || '',
                    svc_3_desc: plazaData.svc_3_desc || ''
                };
            } else if (activeSection === 'clients') {
                fieldsToSave = {
                    clients_title: plazaData.clients_title || '',
                    clients_desc: plazaData.clients_desc || '',
                    clients_list: plazaData.clients_list || '',
                    clients_logo_urls: plazaData.clients_logo_urls || '',
                    clients_footer: plazaData.clients_footer || '',
                    clients_footer_desc: plazaData.clients_footer_desc || ''
                };
            } else if (activeSection === 'contact') {
                fieldsToSave = {
                    contact_form_title: plazaData.contact_form_title || '',
                    contact_info_title: plazaData.contact_info_title || '',
                    contact_info_desc: plazaData.contact_info_desc || '',
                    contact_address: plazaData.contact_address || '',
                    contact_phone: plazaData.contact_phone || '',
                    contact_fax: plazaData.contact_fax || '',
                    contact_support_phone: plazaData.contact_support_phone || '',
                    contact_email: plazaData.contact_email || ''
                };
            }

            // Automatically include Arabic counterparts for all text fields
            const finalFields: { [key: string]: { val: string, ar: string | null } } = {};
            Object.entries(fieldsToSave).forEach(([key, val]) => {
                finalFields[key] = {
                    val: val,
                    ar: plazaData[`${key}_ar`] || null
                };
            });

            const promises = Object.entries(finalFields).map(async ([key, data]) => {
                const response = await fetch('/api/homepage/update-text', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${localStorage.getItem('adminToken')}`
                    },
                    body: JSON.stringify({
                        section: 'Plaza',
                        content_key: key,
                        content_value: data.val,
                        content_value_ar: data.ar
                    })
                });

                if (!response.ok) {
                    const errText = await response.text();
                    console.error(`Field update failed for ${key}: status ${response.status}`, errText);
                }
                return response;
            });

            const results = await Promise.all(promises);
            const allOk = results.every(r => r.ok);
            if (allOk) {
                setMessage({ text: `${activeSection} section updated successfully!`, type: 'success' });
                fetchContent();
            } else {
                const failed = results.filter(r => !r.ok).length;
                setMessage({ text: `${failed} fields failed to save. Check console for details.`, type: 'error' });
            }
        } catch (err) {
            console.error('Save failed', err);
            setMessage({ text: 'Failed to connect to server. Check your connection.', type: 'error' });
        } finally {
            setTimeout(() => {
                setSaving(null);
                setTimeout(() => setMessage({ text: '', type: '' }), 3000);
            }, 500);
        }
    };

    const handleImageSelect = (key: string, file: File) => {
        const preview = URL.createObjectURL(file);
        setPendingImages(prev => ({
            ...prev,
            [key]: { file, preview }
        }));
    };

    const saveImages = async () => {
        setSaving('images');
        setMessage({ text: 'Starting image synchronization...', type: '' });
        try {
            const promises = Object.entries(pendingImages).map(async ([key, pending]) => {
                const formData = new FormData();
                formData.append('section', 'Plaza');
                formData.append('content_key', key);
                formData.append('image', pending.file);

                const response = await fetch('/api/homepage/update-image', {
                    method: 'POST',
                    headers: {
                        'Authorization': `Bearer ${localStorage.getItem('adminToken')}`
                    },
                    body: formData
                });

                if (!response.ok) {
                    const errText = await response.text();
                    console.error(`Upload failed for ${key}:`, errText);
                }
                return response;
            });

            const results = await Promise.all(promises);
            const allOk = results.every(r => r.ok);

            if (allOk) {
                setMessage({ text: 'All images synchronized successfully', type: 'success' });
                setPendingImages({});
                fetchContent();
            } else {
                setMessage({ text: 'Some image uploads failed. Check console for details.', type: 'error' });
            }
        } catch (err) {
            console.error('Image upload failed', err);
            setMessage({ text: 'Connectivity error. Check your server status.', type: 'error' });
        } finally {
            setSaving(null);
            setTimeout(() => setMessage({ text: '', type: '' }), 4000);
        }
    };

    const sections = [
        { id: 'banner', label: 'Hero Banner', icon: Layout },
        { id: 'about', label: 'About Us', icon: FileText },
        { id: 'services', label: 'Services', icon: Wrench },
        { id: 'clients', label: 'Clients', icon: Users },
        { id: 'contact', label: 'Contact', icon: Mail },
    ];

    const UploadBox = ({ keyName, label, currentImage }: any) => {
        const pending = pendingImages[keyName];
        const displayImage = pending ? pending.preview : currentImage;

        return (
            <div className="group bg-white p-6 rounded-2xl border border-slate-100 shadow-sm hover:border-blue-200 transition-all flex flex-col">
                <div className="flex justify-between items-center mb-4">
                    <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">{label}</span>
                    {currentImage && !pending && (
                        <div className="flex items-center gap-1 text-emerald-500 bg-emerald-50 px-2 py-0.5 rounded-lg text-[8px] font-bold uppercase">
                            <CheckCircle2 className="w-3 h-3" /> Live
                        </div>
                    )}
                </div>
                <div className="relative aspect-video bg-slate-50 rounded-xl overflow-hidden mb-4 border border-slate-100">
                    {displayImage ? (
                        <img src={displayImage} className="w-full h-full object-cover" alt="Preview" />
                    ) : (
                        <div className="w-full h-full flex flex-col items-center justify-center text-slate-300">
                            <ImageIcon className="w-8 h-8 opacity-20" />
                        </div>
                    )}
                    <label className="absolute inset-0 cursor-pointer bg-slate-900/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center backdrop-blur-[1px]">
                        <ImageIcon className="w-6 h-6 text-white" />
                        <input type="file" className="hidden" accept="image/*" onChange={e => e.target.files?.[0] && handleImageSelect(keyName, e.target.files[0])} />
                    </label>
                </div>
                {pending && <div className="text-[8px] text-amber-500 font-bold uppercase text-center">Unsaved Changes</div>}
            </div>
        );
    };

    const iconMap: any = { Plane, Star, MapPin };

    return (
        <div className="flex h-screen bg-slate-50 font-sans overflow-hidden">
            <AdminSidebar />

            {/* Main Area */}
            <div className="flex-1 flex flex-col h-full bg-slate-50/50">
                {/* Header */}
                <header className="h-20 bg-white border-b border-slate-200 flex items-center justify-between px-10 shadow-sm shrink-0 z-30">
                    <div className="flex items-center space-x-6">
                        <h2 className="text-xl font-extrabold text-slate-800 flex items-center gap-2">
                            <Building2 className="w-6 h-6 text-blue-600" /> Hotel Editor
                        </h2>

                        <div className="flex bg-slate-100 p-1.5 rounded-2xl">
                            <button
                                onClick={() => setActiveTab('content')}
                                className={`flex items-center space-x-2 px-6 py-2.5 rounded-xl font-bold text-sm transition-all ${activeTab === 'content' ? 'bg-white text-blue-600 shadow-md' : 'text-slate-500 hover:text-slate-700'}`}
                            >
                                <Type className="w-4 h-4" />
                                <span>Content Details</span>
                            </button>
                            <button
                                onClick={() => setActiveTab('images')}
                                className={`flex items-center space-x-2 px-6 py-2.5 rounded-xl font-bold text-sm transition-all ${activeTab === 'images' ? 'bg-white text-blue-600 shadow-md' : 'text-slate-500 hover:text-slate-700'}`}
                            >
                                <ImageIcon className="w-4 h-4" />
                                <span>Media Assets</span>
                            </button>
                        </div>
                    </div>

                    <div className="flex items-center space-x-6">
                        <div className="hidden sm:block text-right">
                            <div className="text-sm font-bold text-slate-900">{username}</div>
                            <div className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Portal Manager</div>
                        </div>
                        <div className="w-10 h-10 bg-slate-100 rounded-full flex items-center justify-center border border-slate-200 shadow-inner">
                            <CheckCircle2 className="w-5 h-5 text-blue-600" />
                        </div>
                    </div>
                </header>

                <main className="flex-1 overflow-y-auto p-10 custom-scrollbar scroll-smooth">
                    <div className="max-w-6xl mx-auto">
                        <AnimatePresence mode="wait">
                            {activeTab === 'content' ? (
                                <motion.div key="content" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }} className="space-y-8">
                                    {/* Section Navigation */}
                                    <div className="flex flex-wrap gap-3 mb-8">
                                        {sections.map(sec => (
                                            <button
                                                key={sec.id}
                                                onClick={() => setActiveSection(sec.id as any)}
                                                className={`flex items-center space-x-2 px-5 py-2.5 rounded-xl font-bold text-xs transition-all border ${activeSection === sec.id ? 'bg-blue-600 border-blue-600 text-white shadow-lg' : 'bg-white border-slate-200 text-slate-500 hover:bg-slate-50'}`}
                                            >
                                                <sec.icon className="w-4 h-4" />
                                                <span>{sec.label}</span>
                                            </button>
                                        ))}
                                    </div>

                                    {message.text && (
                                        <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} className={`p-4 rounded-2xl font-bold text-sm flex items-center gap-3 ${message.type === 'success' ? 'bg-emerald-50 text-emerald-600 border border-emerald-100' : 'bg-red-50 text-red-600 border border-red-100'}`}>
                                            {message.type === 'success' ? <CheckCircle2 className="w-5 h-5" /> : <AlertCircle className="w-5 h-5" />}
                                            {message.text}
                                        </motion.div>
                                    )}

                                    <div className="bg-white p-8 rounded-3xl border border-slate-100 shadow-sm">
                                        <div className="flex items-center justify-between mb-8">
                                            <h3 className="text-lg font-bold text-slate-800 flex items-center capitalize">
                                                <div className="w-1.5 h-6 bg-blue-600 rounded-full mr-3" />
                                                {activeSection} Configuration
                                            </h3>
                                            <button
                                                onClick={saveContent}
                                                disabled={!!saving}
                                                className="flex items-center space-x-2 px-6 py-2.5 bg-emerald-500 hover:bg-emerald-600 disabled:bg-slate-200 text-white rounded-xl font-bold text-sm transition-all shadow-lg active:scale-95"
                                            >
                                                {saving === 'Plaza' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                                                <span>Update {activeSection}</span>
                                            </button>
                                        </div>

                                        {/* Dynamic Section Forms */}
                                        {activeSection === 'banner' && (
                                            <div className="space-y-6">
                                                <BilingualInput 
                                                    label="Banner Title"
                                                    englishValue={content.Plaza?.banner_title ?? 'Hamdan Plaza Hotel'}
                                                    arabicValue={content.Plaza?.banner_title_ar}
                                                    onChangeEnglish={val => updateField('banner_title', val)}
                                                    onChangeArabic={val => updateField('banner_title_ar', val)}
                                                />
                                                <BilingualTextArea 
                                                    label="Banner Subtitle"
                                                    englishValue={content.Plaza?.banner_subtitle ?? 'Three star luxurious property just 5 mins from Salalah International Airport'}
                                                    arabicValue={content.Plaza?.banner_subtitle_ar}
                                                    onChangeEnglish={val => updateField('banner_subtitle', val)}
                                                    onChangeArabic={val => updateField('banner_subtitle_ar', val)}
                                                    rows={3}
                                                />
                                            </div>
                                        )}

                                        {activeSection === 'about' && (
                                            <div className="space-y-6">
                                                <BilingualInput 
                                                    label="Introduction Title"
                                                    englishValue={content.Plaza?.about_title ?? 'Hamdan Plaza Hotel'}
                                                    arabicValue={content.Plaza?.about_title_ar}
                                                    onChangeEnglish={val => updateField('about_title', val)}
                                                    onChangeArabic={val => updateField('about_title_ar', val)}
                                                />
                                                <BilingualTextArea 
                                                    label="Intro Description"
                                                    englishValue={content.Plaza?.about_desc ?? 'Hamdan Plaza Hotel Salalah ; a three star luxurious property just 5 mins drive from Salalah International Airport and City Center. Providing a high standard of hospitalities and various recreational facilities with 168 spacious and elegantly furnished guest rooms & suites.'}
                                                    arabicValue={content.Plaza?.about_desc_ar}
                                                    onChangeEnglish={val => updateField('about_desc', val)}
                                                    onChangeArabic={val => updateField('about_desc_ar', val)}
                                                    rows={6}
                                                />
                                                <div className="space-y-2">
                                                    <label className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">External Website URL</label>
                                                    <input className="w-full p-4 bg-slate-50 border border-slate-100 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none font-bold" value={content.Plaza?.about_website ?? 'www.hamdanplazahotel.com'} onChange={e => updateField('about_website', e.target.value)} />
                                                </div>
                                                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 pt-6 border-t border-slate-100">
                                                    {[
                                                        { id: 1, def_t: 'Prime Location', def_d: '5 minutes from Salalah Airport', def_i: 'Plane' },
                                                        { id: 2, def_t: '3-Star Luxury', def_d: 'Premium hospitality services', def_i: 'Star' },
                                                        { id: 3, def_t: 'Ocean View', def_d: 'Overlooking the Indian Ocean', def_i: 'MapPin' }
                                                    ].map(pt => (
                                                        <div key={pt.id} className="space-y-4 p-4 bg-slate-50 rounded-2xl border border-slate-100">
                                                            <div className="flex justify-between items-center mb-2">
                                                                <span className="text-[10px] font-black text-blue-600 uppercase italic">Feature {pt.id}</span>
                                                                <select className="text-[10px] font-bold rounded-lg border border-slate-200 p-1" value={content.Plaza?.[`about_pt_${pt.id}_icon`] ?? pt.def_i} onChange={e => updateField(`about_pt_${pt.id}_icon`, e.target.value)}>
                                                                    <option value="Plane">Plane</option>
                                                                    <option value="Star">Star</option>
                                                                    <option value="MapPin">MapPin</option>
                                                                    <option value="Globe2">Globe</option>
                                                                </select>
                                                            </div>
                                                            <BilingualInput 
                                                                label="Title"
                                                                englishValue={content.Plaza?.[`about_pt_${pt.id}_title`] ?? pt.def_t}
                                                                arabicValue={content.Plaza?.[`about_pt_${pt.id}_title_ar`]}
                                                                onChangeEnglish={val => updateField(`about_pt_${pt.id}_title`, val)}
                                                                onChangeArabic={val => updateField(`about_pt_${pt.id}_title_ar`, val)}
                                                            />
                                                            <BilingualTextArea 
                                                                label="Description"
                                                                englishValue={content.Plaza?.[`about_pt_${pt.id}_desc`] ?? pt.def_d}
                                                                arabicValue={content.Plaza?.[`about_pt_${pt.id}_desc_ar`]}
                                                                onChangeEnglish={val => updateField(`about_pt_${pt.id}_desc`, val)}
                                                                onChangeArabic={val => updateField(`about_pt_${pt.id}_desc_ar`, val)}
                                                                rows={2}
                                                            />
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        )}

                                        {/* ── SERVICES ── */}
                                        {activeSection === 'services' && (
                                            <ServiceCardManager
                                                section="Plaza"
                                                data={content.Plaza || {}}
                                                onSaved={fetchContent}
                                                sectionTitle={content.Plaza?.services_title ?? 'Our Services'}
                                                sectionTitleAr={content.Plaza?.services_title_ar}
                                                onChangeSectionTitle={val => updateField('services_title', val)}
                                                onChangeSectionTitleAr={val => updateField('services_title_ar', val)}
                                            />
                                        )}

                                        {activeSection === 'clients' && (
                                            <ClientsEditorFields
                                                data={content.Plaza}
                                                updateField={updateField}
                                                defaultDescription="Corporate and hospitality clients served by Hamdan Plaza Hotel."
                                            />
                                        )}

                                        {activeSection === 'contact' && (
                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                                                <div className="space-y-6">
                                                    <BilingualInput 
                                                        label="Form Title"
                                                        englishValue={content.Plaza?.contact_form_title ?? 'Send Us Email'}
                                                        arabicValue={content.Plaza?.contact_form_title_ar}
                                                        onChangeEnglish={val => updateField('contact_form_title', val)}
                                                        onChangeArabic={val => updateField('contact_form_title_ar', val)}
                                                    />
                                                    <BilingualInput 
                                                        label="Info Title"
                                                        englishValue={content.Plaza?.contact_info_title ?? 'Get in Touch'}
                                                        arabicValue={content.Plaza?.contact_info_title_ar}
                                                        onChangeEnglish={val => updateField('contact_info_title', val)}
                                                        onChangeArabic={val => updateField('contact_info_title_ar', val)}
                                                    />
                                                    <BilingualTextArea 
                                                        label="Info Description"
                                                        englishValue={content.Plaza?.contact_info_desc ?? 'We appreciate your interest in giving to Hamdan Plaza Hotel, and we want to make it as easy as possible for you to get your queries answered.'}
                                                        arabicValue={content.Plaza?.contact_info_desc_ar}
                                                        onChangeEnglish={val => updateField('contact_info_desc', val)}
                                                        onChangeArabic={val => updateField('contact_info_desc_ar', val)}
                                                        rows={4}
                                                    />
                                                </div>
                                                <div className="bg-slate-50 p-6 rounded-3xl border border-slate-100 space-y-4">
                                                    <span className="text-[10px] font-black text-blue-600 uppercase">Contact Information</span>
                                                    <div className="space-y-4">
                                                        {[
                                                            { id: 'address', l: 'Address', d: 'Salalah, Sultanate of Oman' },
                                                            { id: 'phone', l: 'Phone', d: '+(968) 23-211024 / 23-211025' },
                                                            { id: 'fax', l: 'Fax', d: '+(968) 23-211187' },
                                                            { id: 'support_phone', l: 'Support Phone', d: '+(968) 23-211026 / 23-211027' },
                                                            { id: 'email', l: 'Email', d: 'reservation@hamdanplazahotel.com' }
                                                        ].map(field => (
                                                            <div key={field.id} className="flex flex-col space-y-2 bg-white p-3 rounded-2xl border border-slate-200">
                                                                <BilingualInput 
                                                                    label={field.l}
                                                                    englishValue={content.Plaza?.[`contact_${field.id}`] ?? field.d}
                                                                    arabicValue={content.Plaza?.[`contact_${field.id}_ar`]}
                                                                    onChangeEnglish={val => updateField(`contact_${field.id}`, val)}
                                                                    onChangeArabic={val => updateField(`contact_${field.id}_ar`, val)}
                                                                />
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </motion.div>
                            ) : (
                                <motion.div key="images" initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.95 }} className="space-y-8">
                                    {message.text && (
                                        <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} className={`p-4 rounded-2xl font-bold text-sm flex items-center gap-3 ${message.type === 'success' ? 'bg-emerald-50 text-emerald-600 border border-emerald-100' : 'bg-red-50 text-red-600 border border-red-100'}`}>
                                            {message.type === 'success' ? <CheckCircle2 className="w-5 h-5" /> : <AlertCircle className="w-5 h-5" />}
                                            {message.text}
                                        </motion.div>
                                    )}
                                    <div className="flex items-center justify-between mb-6">
                                        <div className="bg-white p-6 rounded-3xl border border-slate-100 shadow-sm flex-1 mr-6">
                                            <h3 className="text-xl font-black text-slate-800 tracking-tight">Media Management</h3>
                                            <p className="text-xs text-slate-400 font-bold uppercase tracking-widest mt-1">Global Batch Uploader</p>
                                        </div>
                                        <button
                                            onClick={saveImages}
                                            disabled={Object.keys(pendingImages).length === 0 || !!saving}
                                            className="px-10 py-5 bg-blue-600 hover:bg-blue-700 disabled:bg-slate-200 text-white rounded-3xl font-black text-xs uppercase tracking-widest transition-all shadow-2xl shadow-blue-100 flex items-center space-x-3"
                                        >
                                            {saving === 'images' ? <Loader2 className="w-5 h-5 animate-spin" /> : <RefreshCw className="w-5 h-5" />}
                                            <span>Synchronize {Object.keys(pendingImages).length} Assets</span>
                                        </button>
                                    </div>

                                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                                        <UploadBox keyName="banner_1" label="Hero Banner 01" currentImage={content.Plaza?.banner_1 ?? '/banner/hamdanPlaza_1.jpg'} />
                                        <UploadBox keyName="banner_2" label="Hero Banner 02" currentImage={content.Plaza?.banner_2 ?? '/banner/hamdanPlaza_2.jpg'} />
                                        <UploadBox keyName="banner_3" label="Hero Banner 03" currentImage={content.Plaza?.banner_3 ?? '/banner/hamdanPlaza_3.jpg'} />
                                        <UploadBox keyName="about_image" label="Hotel Logo/Image" currentImage={content.Plaza?.about_image ?? '/Hamdan-Plaza-logo.jpg'} />
                                        <UploadBox keyName="svc_1_image" label="Service 1 Image" currentImage={content.Plaza?.svc_1_image} />
                                        <UploadBox keyName="svc_2_image" label="Service 2 Image" currentImage={content.Plaza?.svc_2_image} />
                                        <UploadBox keyName="svc_3_image" label="Service 3 Image" currentImage={content.Plaza?.svc_3_image} />
                                    </div>

                                    <div className="mt-12">
                                        <ClientLogoUploadGrid
                                            data={content.Plaza}
                                            pendingImages={pendingImages}
                                            onSelect={handleImageSelect}
                                        />
                                    </div>
                                </motion.div>
                            )}
                        </AnimatePresence>
                    </div>
                </main>
            </div>
        </div>
    );
};

export default AdminPlaza;
